/*
 * bg-collection-filter.js — Filterleiste, Preis-Umschalter und Produktraster
 * Bogey Golf
 *
 * Holt einmalig /collections/<handle>/products.json (alle Produkte der Kollektion),
 * filtert im Browser und rendert das Raster selbst. Gründe siehe Section-Kommentar.
 *
 * Kernidee: Qualität und Menge filtern nicht, sie stellen den Preis ein. Alle Preise
 * und der Preis-Slider beziehen sich auf die gewählte Kombination.
 *
 * CLS 28.10.2026: Die Section liefert Umschalter, Leiste und die ersten Karten schon
 * fertig aus (data-bg-ssr). Beim Start bleibt dieses Raster stehen, wenn es mit dem
 * übereinstimmt, was render() bauen würde — erst eine Auswahl des Kunden baut neu.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'bg-filter-choice';
  var root, results, cfg;
  var products = [];
  var state = null;
  var shown = 0;

  /* ---------- Hilfen ---------- */

  function euro(value) {
    var whole = Math.abs(value).toFixed(2).split('.');
    var head = whole[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return head + ',' + whole[1];
  }

  function money(value) {
    var format = cfg.moneyFormat || '€{{amount_with_comma_separator}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, euro(value));
  }

  function imageUrl(src, width) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* Packungsgröße aus dem Titel, für Produkte ohne Mengen-Option
     („100 Marken-Mix …", „Übungsbälle 50er-Pack") */
  function guessCount(title) {
    var match = /(\d{1,4})\s*(?:er)?\b/.exec(title || '');
    if (!match) return null;
    var value = parseInt(match[1], 10);
    return value >= 4 ? value : null;
  }

  function modelName(product) {
    var title = product.title;
    if (product.vendor && title.indexOf(product.vendor) === 0) {
      var rest = title.slice(product.vendor.length).trim();
      if (rest) return rest;
    }
    return title;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ---------- Daten aufbereiten ---------- */

  function normalise(raw) {
    var list = [];

    raw.forEach(function (item) {
      var gradeIndex = -1;
      var qtyIndex = -1;

      (item.options || []).forEach(function (option, index) {
        if (option.name === cfg.gradeOption) gradeIndex = index;
        if (option.name === cfg.qtyOption) qtyIndex = index;
      });

      /* Produkte ohne Qualität/Menge (fertige Mix-Pakete) laufen als einfache
         Produkte mit festem Preis mit — sie sollen nicht aus der Liste fallen. */
      if (gradeIndex < 0 || qtyIndex < 0) {
        var simple = [];
        (item.variants || []).forEach(function (variant) {
          simple.push({
            grade: null,
            count: guessCount(item.title),
            price: parseFloat(variant.price),
            compareAt: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
            id: variant.id,
            available: variant.available !== false
          });
        });
        if (!simple.length) return;

        simple.sort(function (a, b) {
          return a.price - b.price;
        });
        list.push({
          handle: item.handle,
          title: item.title,
          model: modelName(item),
          vendor: item.vendor,
          image: item.images && item.images.length ? item.images[0].src : null,
          variants: simple,
          simple: true,
          available: simple.some(function (v) {
            return v.available;
          })
        });
        return;
      }

      var variants = [];
      (item.variants || []).forEach(function (variant) {
        var values = [variant.option1, variant.option2, variant.option3];
        var grade = values[gradeIndex];
        var count = parseInt(values[qtyIndex], 10);
        if (!grade || !count) return;

        variants.push({
          grade: grade,
          count: count,
          price: parseFloat(variant.price),
          compareAt: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
          id: variant.id,
          available: variant.available !== false
        });
      });
      if (!variants.length) return;

      list.push({
        handle: item.handle,
        title: item.title,
        model: modelName(item),
        vendor: item.vendor,
        image: item.images && item.images.length ? item.images[0].src : null,
        variants: variants,
        available: variants.some(function (v) {
          return v.available;
        })
      });
    });

    return list;
  }

  /* Passende Variante zur aktuellen Auswahl, sonst nächstgrößere Packung */
  function variantFor(product) {
    /* Feste Pakete kennen keine Qualität/Menge — sie behalten ihren Preis */
    if (product.simple) {
      return { variant: product.variants[0], exact: true, simple: true };
    }

    var same = [];
    var exact = null;

    product.variants.forEach(function (v) {
      if (v.grade !== state.grade) return;
      same.push(v);
      if (v.count === state.qty) exact = v;
    });
    if (exact) return { variant: exact, exact: true };
    if (!same.length) return null;

    same.sort(function (a, b) {
      return a.count - b.count;
    });
    var up = null;
    for (var i = 0; i < same.length; i++) {
      if (same[i].count > state.qty) {
        up = same[i];
        break;
      }
    }
    return { variant: up || same[same.length - 1], exact: false };
  }

  /* ---------- Zustand ---------- */

  function defaultState() {
    return {
      grade: cfg.defaultGrade,
      qty: cfg.defaultQty,
      vendors: [],
      models: [],
      min: null,
      max: null,
      inStockOnly: false,
      sort: 'recommended'
    };
  }

  function loadState() {
    var next = defaultState();
    try {
      var saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved) {
        if (cfg.grades.indexOf(saved.grade) > -1) next.grade = saved.grade;
        if (cfg.quantities.indexOf(saved.qty) > -1) next.qty = saved.qty;
      }
    } catch (e) {
      /* Voreinstellung genügt */
    }
    return next;
  }

  function saveState() {
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ grade: state.grade, qty: state.qty })
      );
    } catch (e) {
      /* Komfort, kein Muss */
    }
  }

  /* ---------- Filtern ---------- */

  /* Alles außer dem Preis — Basis für die Spannweite des Sliders */
  function matchesBase(product) {
    if (state.vendors.length && state.vendors.indexOf(product.vendor) < 0) return false;
    if (state.models.length && state.models.indexOf(product.handle) < 0) return false;
    if (state.inStockOnly && !product.available) return false;
    return true;
  }

  function priceRange(list) {
    var min = null;
    var max = null;
    list.forEach(function (product) {
      var hit = variantFor(product);
      if (!hit) return;
      var price = hit.variant.price;
      if (min === null || price < min) min = price;
      if (max === null || price > max) max = price;
    });
    if (min === null) return { min: 0, max: 0 };
    return { min: Math.floor(min), max: Math.ceil(max) };
  }

  function currentList() {
    var base = products.filter(matchesBase);
    if (state.min === null && state.max === null) return base;

    return base.filter(function (product) {
      var hit = variantFor(product);
      if (!hit) return false;
      var price = hit.variant.price;
      if (state.min !== null && price < state.min) return false;
      if (state.max !== null && price > state.max) return false;
      return true;
    });
  }

  function sortList(list) {
    var out = list.slice();
    if (state.sort === 'price-asc' || state.sort === 'price-desc') {
      out.sort(function (a, b) {
        var pa = variantFor(a);
        var pb = variantFor(b);
        var va = pa ? pa.variant.price : 0;
        var vb = pb ? pb.variant.price : 0;
        return state.sort === 'price-asc' ? va - vb : vb - va;
      });
    } else if (state.sort === 'title-asc') {
      out.sort(function (a, b) {
        return a.title.localeCompare(b.title, 'de');
      });
    } else {
      /* Empfohlen: Lieferbares zuerst, sonst Reihenfolge der Kollektion */
      out.sort(function (a, b) {
        return (b.available ? 1 : 0) - (a.available ? 1 : 0);
      });
    }
    return out;
  }

  /* ---------- Bedienelemente ---------- */

  function pills(container, values, current, onPick) {
    container.innerHTML = '';
    values.forEach(function (value) {
      var button = el('button', 'bg-pill', String(value));
      button.type = 'button';
      button.setAttribute('aria-pressed', String(value === current));
      if (typeof value === 'number') button.setAttribute('aria-label', value + ' Bälle');
      button.addEventListener('click', function () {
        onPick(value);
      });
      container.appendChild(button);
    });
  }

  function closePanels(except) {
    Array.prototype.forEach.call(root.querySelectorAll('.bg-drop'), function (drop) {
      if (drop === except) return;
      var button = drop.querySelector('.bg-drop__btn');
      var panel = drop.querySelector('.bg-drop__panel');
      if (button) button.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
    });
  }

  function makeDrop(label, count, build, opts) {
    opts = opts || {};
    var wrap = el('div', 'bg-drop');
    var button = el('button', 'bg-drop__btn');
    button.type = 'button';
    button.setAttribute('aria-expanded', 'false');

    if (opts.icon) {
      var icon = el('span', 'bg-drop__icon');
      icon.innerHTML = opts.icon;
      button.appendChild(icon);
    }
    /* Auf schmalen Displays entfällt der Zusatz, das Icon trägt die Bedeutung */
    if (opts.prefix) {
      button.appendChild(el('span', 'bg-drop__prefix', opts.prefix));
    }
    button.appendChild(el('span', 'bg-drop__label', label));

    if (count) {
      button.appendChild(el('span', 'bg-drop__count', String(count)));
      button.classList.add('is-active');
    }
    button.appendChild(el('span', 'bg-chev'));

    var panel = el('div', 'bg-drop__panel');
    panel.hidden = true;
    build(panel);

    button.addEventListener('click', function (event) {
      event.stopPropagation();
      var open = button.getAttribute('aria-expanded') === 'true';
      closePanels(wrap);
      button.setAttribute('aria-expanded', String(!open));
      panel.hidden = open;
    });
    panel.addEventListener('click', function (event) {
      event.stopPropagation();
    });

    wrap.appendChild(button);
    wrap.appendChild(panel);
    return wrap;
  }

  function option(labelText, checked, count, onToggle) {
    var label = el('label', 'bg-opt');
    var box = document.createElement('input');
    box.type = 'checkbox';
    box.checked = checked;
    box.addEventListener('change', function () {
      onToggle(box.checked);
    });
    label.appendChild(box);
    label.appendChild(el('span', null, labelText));
    if (count != null) {
      label.appendChild(el('span', 'bg-opt__n', String(count)));
      if (count === 0) label.classList.add('is-empty');
    }
    return label;
  }

  function buildBar() {
    var bar = root.querySelector('[data-bg-bar]');
    bar.innerHTML = '';

    /* --- Marke: Mehrfachauswahl --- */
    var vendorCounts = {};
    products.forEach(function (p) {
      vendorCounts[p.vendor] = (vendorCounts[p.vendor] || 0) + 1;
    });
    var vendors = Object.keys(vendorCounts).sort(function (a, b) {
      return vendorCounts[b] - vendorCounts[a] || a.localeCompare(b, 'de');
    });

    bar.appendChild(
      makeDrop('Marke', state.vendors.length, function (panel) {
        vendors.forEach(function (vendor) {
          panel.appendChild(
            option(vendor, state.vendors.indexOf(vendor) > -1, vendorCounts[vendor], function (on) {
              if (on) state.vendors.push(vendor);
              else state.vendors.splice(state.vendors.indexOf(vendor), 1);
              state.models = state.models.filter(function (handle) {
                var product = products.filter(function (p) {
                  return p.handle === handle;
                })[0];
                return (
                  product && (!state.vendors.length || state.vendors.indexOf(product.vendor) > -1)
                );
              });
              resetPrice();
              rebuild();
            })
          );
        });
      })
    );

    /* --- Modell: hängt an der Markenauswahl --- */
    var pool = products.filter(function (p) {
      return !state.vendors.length || state.vendors.indexOf(p.vendor) > -1;
    });

    bar.appendChild(
      makeDrop('Modell', state.models.length, function (panel) {
        var all = el('label', 'bg-opt');
        var allBox = document.createElement('input');
        allBox.type = 'checkbox';
        allBox.checked = state.models.length === 0;
        allBox.addEventListener('change', function () {
          state.models = [];
          resetPrice();
          rebuild();
        });
        all.appendChild(allBox);
        all.appendChild(el('span', null, 'Alle Modelle'));
        all.appendChild(el('span', 'bg-opt__n', String(pool.length)));
        panel.appendChild(all);

        pool
          .slice()
          .sort(function (a, b) {
            return a.vendor.localeCompare(b.vendor, 'de') || a.model.localeCompare(b.model, 'de');
          })
          .forEach(function (product) {
            var text =
              state.vendors.length === 1 ? product.model : product.vendor + ' ' + product.model;
            panel.appendChild(
              option(text, state.models.indexOf(product.handle) > -1, null, function (on) {
                if (on) state.models.push(product.handle);
                else state.models.splice(state.models.indexOf(product.handle), 1);
                resetPrice();
                rebuild();
              })
            );
          });

        if (!state.vendors.length) {
          panel.appendChild(
            el('p', 'bg-drop__hint', 'Wähle zuerst eine Marke, dann wird die Liste kürzer.')
          );
        }
      })
    );

    /* --- Preis: Slider auf dem Preis der gewählten Qualität × Menge --- */
    var bounds = priceRange(products.filter(matchesBase));
    var active = state.min !== null || state.max !== null;

    bar.appendChild(
      makeDrop('Preis', active ? 1 : 0, function (panel) {
        panel.appendChild(buildRange(bounds));
      })
    );

    /* --- Verfügbarkeit --- */
    var stock = el(
      'button',
      'bg-drop__btn' + (state.inStockOnly ? ' is-active' : ''),
      'Nur lieferbare'
    );
    stock.type = 'button';
    stock.setAttribute('aria-pressed', String(state.inStockOnly));
    stock.addEventListener('click', function () {
      state.inStockOnly = !state.inStockOnly;
      resetPrice();
      rebuild();
    });
    bar.appendChild(stock);

    /* --- Sortierung --- */
    var sorts = [
      ['recommended', 'Empfohlen'],
      ['price-asc', 'Preis aufsteigend'],
      ['price-desc', 'Preis absteigend'],
      ['title-asc', 'Name A–Z']
    ];
    var current = sorts.filter(function (s) {
      return s[0] === state.sort;
    })[0];

    var sortIcon =
      '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.75" stroke-linecap="round" aria-hidden="true" focusable="false">' +
      '<path d="M4 7h13" /><path d="M4 12h9" /><path d="M4 17h5" /></svg>';

    bar.appendChild(
      makeDrop(
        current ? current[1] : 'Empfohlen',
        0,
        function (panel) {
          sorts.forEach(function (entry) {
            var label = el('label', 'bg-opt');
            var box = document.createElement('input');
            box.type = 'checkbox';
            box.checked = state.sort === entry[0];
            box.addEventListener('change', function () {
              state.sort = entry[0];
              rebuild();
            });
            label.appendChild(box);
            label.appendChild(el('span', null, entry[1]));
            panel.appendChild(label);
          });
        },
        { icon: sortIcon, prefix: 'Sortieren:' }
      )
    );
  }

  function buildRange(bounds) {
    var wrap = el('div', 'bg-range');
    var span = Math.max(1, bounds.max - bounds.min);
    var from = state.min === null ? bounds.min : state.min;
    var to = state.max === null ? bounds.max : state.max;

    var values = el('div', 'bg-range__values');
    var fromLabel = el('span', null, money(from));
    var toLabel = el('span', null, money(to));
    values.appendChild(fromLabel);
    values.appendChild(toLabel);

    var track = el('div', 'bg-range__track');
    var rail = el('div', 'bg-range__rail');
    var fill = el('div', 'bg-range__fill');
    track.appendChild(rail);
    track.appendChild(fill);

    var minInput = document.createElement('input');
    var maxInput = document.createElement('input');
    [minInput, maxInput].forEach(function (input) {
      input.type = 'range';
      input.min = String(bounds.min);
      input.max = String(bounds.max);
      input.step = '1';
    });
    minInput.value = String(from);
    maxInput.value = String(to);
    minInput.setAttribute('aria-label', 'Mindestpreis');
    maxInput.setAttribute('aria-label', 'Höchstpreis');

    function paint() {
      var lo = Math.min(+minInput.value, +maxInput.value);
      var hi = Math.max(+minInput.value, +maxInput.value);
      fill.style.left = ((lo - bounds.min) / span) * 100 + '%';
      fill.style.right = ((bounds.max - hi) / span) * 100 + '%';
      fromLabel.textContent = money(lo);
      toLabel.textContent = money(hi);
    }

    function commit() {
      var lo = Math.min(+minInput.value, +maxInput.value);
      var hi = Math.max(+minInput.value, +maxInput.value);
      state.min = lo <= bounds.min ? null : lo;
      state.max = hi >= bounds.max ? null : hi;
      shown = cfg.perPage;
      render();
      updateActive();
    }

    minInput.addEventListener('input', paint);
    maxInput.addEventListener('input', paint);
    minInput.addEventListener('change', commit);
    maxInput.addEventListener('change', commit);

    track.appendChild(minInput);
    track.appendChild(maxInput);
    wrap.appendChild(values);
    wrap.appendChild(track);
    wrap.appendChild(
      el(
        'p',
        'bg-range__note',
        'Preis für ' +
          state.grade +
          ' · ' +
          state.qty +
          ' Bälle. Beim Wechsel von Qualität oder Menge stellt sich die Spanne neu ein.'
      )
    );

    paint();
    return wrap;
  }

  function resetPrice() {
    state.min = null;
    state.max = null;
  }

  /* ---------- Aktive Filter ---------- */

  function updateActive() {
    var box = root.querySelector('[data-bg-active]');
    box.innerHTML = '';
    var chips = [];

    state.vendors.forEach(function (vendor) {
      chips.push([
        vendor,
        function () {
          state.vendors.splice(state.vendors.indexOf(vendor), 1);
        }
      ]);
    });
    state.models.forEach(function (handle) {
      var product = products.filter(function (p) {
        return p.handle === handle;
      })[0];
      chips.push([
        product ? product.title : handle,
        function () {
          state.models.splice(state.models.indexOf(handle), 1);
        }
      ]);
    });
    if (state.min !== null || state.max !== null) {
      var bounds = priceRange(products.filter(matchesBase));
      var text =
        money(state.min === null ? bounds.min : state.min) +
        ' – ' +
        money(state.max === null ? bounds.max : state.max);
      chips.push([text, resetPrice]);
    }
    if (state.inStockOnly) {
      chips.push([
        'Nur lieferbare',
        function () {
          state.inStockOnly = false;
        }
      ]);
    }

    if (!chips.length) {
      box.hidden = true;
      return;
    }
    box.hidden = false;

    chips.forEach(function (entry) {
      var chip = el('span', 'bg-chip');
      chip.appendChild(el('span', null, entry[0]));
      var close = el('button', null, '×');
      close.type = 'button';
      close.setAttribute('aria-label', entry[0] + ' entfernen');
      close.addEventListener('click', function () {
        entry[1]();
        rebuild();
      });
      chip.appendChild(close);
      box.appendChild(chip);
    });

    var reset = el('button', 'bg-reset', 'Alle zurücksetzen');
    reset.type = 'button';
    reset.addEventListener('click', function () {
      var grade = state.grade;
      var qty = state.qty;
      state = defaultState();
      state.grade = grade;
      state.qty = qty;
      rebuild();
    });
    box.appendChild(reset);
  }

  /* ---------- Raster ---------- */

  /* bare: ohne Bild — zum Vergleichen mit dem Server-Raster, ohne Bilder anzufordern */
  function card(product, bare) {
    var hit = variantFor(product);
    if (!hit) return null;

    var variant = hit.variant;
    var item = el('li', 'bg-card');
    var link = el('a', 'bg-card__link');
    link.href = '/products/' + product.handle + '?variant=' + variant.id;

    var media = el('div', 'bg-card__media');
    if (product.image && !bare) {
      var img = document.createElement('img');
      img.src = imageUrl(product.image, 600);
      img.srcset =
        imageUrl(product.image, 400) + ' 400w, ' + imageUrl(product.image, 800) + ' 800w';
      img.sizes = '(min-width: 990px) 25vw, 50vw';
      img.alt = product.title;
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      media.appendChild(img);
    }

    if (!product.simple) {
      var isLast = state.grade === cfg.grades[cfg.grades.length - 1];
      media.appendChild(
        el('span', 'bg-card__badge' + (isLast ? ' bg-card__badge--classic' : ''), state.grade)
      );
    }

    if (!variant.available) {
      var out = el('div', 'bg-card__soldout');
      out.appendChild(el('span', null, 'Ausverkauft'));
      media.appendChild(out);
    }
    link.appendChild(media);

    link.appendChild(el('div', 'bg-card__vendor', product.vendor));
    link.appendChild(el('h3', 'bg-card__name', product.title));

    var spec = product.simple
      ? variant.count
        ? variant.count + ' Bälle · festes Paket'
        : 'Festes Paket'
      : state.grade + ' · ' + variant.count + ' Bälle';
    link.appendChild(el('p', 'bg-card__spec', spec));

    var prices = el('div', 'bg-card__prices');
    prices.appendChild(el('span', 'bg-card__price', money(variant.price)));
    if (variant.compareAt && variant.compareAt > variant.price) {
      prices.appendChild(el('span', 'bg-card__was', money(variant.compareAt)));
    }
    link.appendChild(prices);

    if (!hit.exact) {
      link.appendChild(
        el(
          'div',
          'bg-card__note',
          'nicht als ' + state.qty + 'er — Preis für ' + variant.count + ' Bälle'
        )
      );
    }

    item.appendChild(link);
    return item;
  }

  function render(keepGrid) {
    var list = sortList(currentList());
    var grid = results.querySelector('[data-bg-grid]');
    var more = results.querySelector('[data-bg-more]');
    var empty = results.querySelector('[data-bg-empty]');

    if (shown <= 0) shown = cfg.perPage;
    var slice = list.slice(0, shown);

    if (!keepGrid) {
      grid.innerHTML = '';
      slice.forEach(function (product) {
        var node = card(product);
        if (node) grid.appendChild(node);
      });
    }

    empty.hidden = list.length > 0;

    if (list.length > slice.length) {
      more.hidden = false;
      more.textContent =
        'Weitere ' + Math.min(cfg.perPage, list.length - slice.length) + ' anzeigen';
    } else {
      more.hidden = true;
    }

    root.querySelector('[data-bg-count]').innerHTML =
      '<b>' +
      list.length +
      '</b> von ' +
      products.length +
      ' Produkten · Preise für <b>' +
      state.grade +
      ' · ' +
      state.qty +
      ' Bälle</b>';
  }

  function rebuild(keepGrid) {
    shown = cfg.perPage;
    buildBar();
    updateActive();
    render(keepGrid);
  }

  function squash(text) {
    return (text || '').replace(/\s+/g, '');
  }

  /* Stimmt das vom Server gerenderte Raster Karte für Karte (Link und Text) mit dem
     überein, was render() jetzt bauen würde? Nur dann bleibt es stehen. Weicht etwas ab
     (gespeicherte Auswahl, ?sort_by, geänderte Preise), wird wie früher neu gerendert. */
  function serverGridMatches() {
    if (!root.hasAttribute('data-bg-ssr')) return false;
    root.removeAttribute('data-bg-ssr');

    var existing = results.querySelector('[data-bg-grid]').children;
    var fresh = [];
    sortList(currentList())
      .slice(0, cfg.perPage)
      .forEach(function (product) {
        var node = card(product, true);
        if (node) fresh.push(node);
      });
    if (existing.length !== fresh.length) return false;

    for (var i = 0; i < fresh.length; i++) {
      var a = existing[i].querySelector('.bg-card__link');
      var b = fresh[i].querySelector('.bg-card__link');
      if (!a || a.getAttribute('href') !== b.getAttribute('href')) return false;
      if (squash(a.textContent) !== squash(b.textContent)) return false;
    }
    return true;
  }

  /* ---------- Start ---------- */

  function buildSwitcher() {
    pills(root.querySelector('[data-bg-grades]'), cfg.grades, state.grade, function (value) {
      state.grade = value;
      resetPrice();
      saveState();
      buildSwitcher();
      rebuild();
    });
    pills(root.querySelector('[data-bg-quantities]'), cfg.quantities, state.qty, function (value) {
      state.qty = value;
      resetPrice();
      saveState();
      buildSwitcher();
      rebuild();
    });
  }

  function hideThemeGrid() {
    ['#ProductGridContainer', '#main-collection-filters'].forEach(function (selector) {
      var node = document.querySelector(selector);
      if (node) node.classList.add('bg-theme-hidden');
    });
  }

  function init() {
    root = document.querySelector('[data-bg-filter]');
    results = document.querySelector('[data-bg-results]');
    if (!root || !results) return;

    cfg = {
      collection: root.getAttribute('data-collection'),
      gradeOption: root.getAttribute('data-grade-option'),
      qtyOption: root.getAttribute('data-qty-option'),
      defaultGrade: root.getAttribute('data-default-grade'),
      defaultQty: parseInt(root.getAttribute('data-default-qty'), 10),
      perPage: parseInt(root.getAttribute('data-per-page'), 10) || 36,
      moneyFormat: root.getAttribute('data-money-format'),
      quantities: (root.getAttribute('data-quantities') || '')
        .split(',')
        .map(function (n) {
          return parseInt(n, 10);
        })
        .filter(function (n) {
          return n > 0;
        })
        .sort(function (a, b) {
          return a - b;
        }),
      grades: []
    };
    if (!cfg.collection || !cfg.quantities.length) return;

    window
      .fetch('/collections/' + cfg.collection + '/products.json?limit=250', {
        headers: { Accept: 'application/json' }
      })
      .then(function (response) {
        if (!response.ok) throw new Error('products.json ' + response.status);
        return response.json();
      })
      .then(function (payload) {
        products = normalise(payload.products || []);

        /* Nur übernehmen, wenn die Kollektion wirklich Lakeballs mit Qualität und
           Menge führt. Auf Zubehör-Kollektionen bleibt das Theme-Raster stehen. */
        var graded = products.filter(function (p) {
          return !p.simple;
        });
        if (graded.length < 2) return;

        var grades = {};
        graded.forEach(function (p) {
          p.variants.forEach(function (v) {
            if (v.grade) grades[v.grade] = true;
          });
        });
        cfg.grades = Object.keys(grades).sort();
        if (cfg.grades.indexOf(cfg.defaultGrade) < 0) cfg.defaultGrade = cfg.grades[0];
        if (cfg.quantities.indexOf(cfg.defaultQty) < 0) cfg.defaultQty = cfg.quantities[0];

        state = loadState();

        hideThemeGrid();
        root.hidden = false;
        results.hidden = false;

        buildSwitcher();
        rebuild(serverGridMatches());

        results.querySelector('[data-bg-more]').addEventListener('click', function () {
          shown += cfg.perPage;
          render();
        });
        document.addEventListener('click', function () {
          /* Nach dem Schließen die Zähler an den Knöpfen auffrischen */
          var open = root.querySelector('.bg-drop__btn[aria-expanded="true"]');
          closePanels(null);
          if (open) buildBar();
        });
        document.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') closePanels(null);
        });
      })
      .catch(function () {
        /* Bleibt still: Das Theme-Raster ist noch da und funktioniert */
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
